import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "react-query";

import {
  IPartnerCreateParams,
  IPartnerUpdateParams,
} from "~/entity/Partners/model/schema";
import { useAddNotificationMutation } from "~/schemas/generated/graphql";
import { useNotify } from "~/shared/hooks/useNotify";
import { defaultErrorHandlerFunc } from "~/shared/hooks/useRequestHandler";

import { usePartners } from "./usePartners";

export const useSetPartner = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [addNotificationMutation] = useAddNotificationMutation({});
  const { notifySuccess } = useNotify();
  const { queryKeys, createPartner, updatePartner, deletePartner } =
    usePartners();

  const invalidatePartners = () => {
    queryClient.invalidateQueries(queryKeys.all);
  };

  const createPartnerMutation = useMutation(
    (data: IPartnerCreateParams) => createPartner(data),
    {
      onSuccess: () => {
        notifySuccess(t("partners.notification.createSuccess"));
        invalidatePartners();
      },
      onError: (e) => {
        defaultErrorHandlerFunc({ e, t, addNotificationMutation });
      },
    },
  );

  const updatePartnerMutation = useMutation(
    ({ id, data }: { id: string; data: IPartnerUpdateParams }) =>
      updatePartner(id, data),
    {
      onSuccess: (_, { id }) => {
        notifySuccess(t("partners.notification.updateSuccess"));
        invalidatePartners();
        queryClient.invalidateQueries(queryKeys.byId(id));
      },
      onError: (e) => {
        defaultErrorHandlerFunc({ e, t, addNotificationMutation });
      },
    },
  );

  const deletePartnerMutation = useMutation((id: string) => deletePartner(id), {
    onSuccess: () => {
      notifySuccess(t("partners.notification.deleteSuccess"));
      invalidatePartners();
    },
    onError: (e) => {
      defaultErrorHandlerFunc({ e, t, addNotificationMutation });
    },
  });

  return {
    createPartner: createPartnerMutation,
    updatePartner: updatePartnerMutation,
    deletePartner: deletePartnerMutation,
  };
};
